<template>
  <UIModal
    v-model="isOpen"
    :close-button="!simplified"
    :close-on-click-outside="!simplified"
    :front-blur="simplified"
    @closed="simplified ? null : emit('closed')"
  >
    <template #header>
      <div class="modal-header">
        <div class="modal-header-primary">{{ $t('halloweenModalExpeditionLocationSelectTitle') }}</div>
        <div class="modal-header-secondary">{{ $t('halloweenModalExpeditionLocationSelectSubtitle') }}</div>
      </div>
    </template>

    <div :class="['modal-body', { switching: isSwitching, simplified }]">
      <div
        class="modal-body-background"
        :style="{ backgroundImage: `url(/img/halloween/bg/bg_expedition_select_${location}.png)` }"
      />

      <div v-if="!simplified" class="modal-body-wrapper">
        <div class="accordion">
          <UIHalloweenAccordion
            :panel-inset-sides="0"
            :panel-inset-top="0"
            :panel-inset-bottom="0"
            :chip-top="10"
            :chip-left="10"
            :model-value="true"
            :panel-pad-bottom="10"
            :panel-pad-top="10"
            :panel-pad-left="10"
            :panel-pad-right="10"
            static
          >
            <template #chip>
              {{ $t(location) }}
            </template>

            <template #title>
              {{ $t(location) }}
            </template>

            <div class="accordion-content">
              <div
                v-for="(paragraph, i) in locationDescription"
                :key="i"
                class="tw:text-[#DEDEDE] tw:text-lg tw:leading-5"
                v-html="paragraph"
              />
              <HalloweenItemFrame variant="default">
                <div v-if="locationConfig" class="accordion-content-craft-frame">
                  <UIItemDisplay
                    :size="30"
                    :item="halloweenItemRegistry.get(HalloweenItem.PirateHead)"
                    direction="horizontal"
                    count="1-5"
                    variant="default"
                    count-size="large"
                  />

                  <UIItemDisplay
                    v-for="item in locationConfig.config"
                    :key="item.address"
                    :size="30"
                    :variant="(locationTokenBalances[item.address.toLowerCase()] || 0) > 0 ? 'default' : 'error'"
                    :item="item"
                    direction="horizontal"
                    count-size="large"
                    :count="`${locationTokenBalances[item.address.toLowerCase()] || 0}`"
                  >
                    <template v-if="(locationTokenBalances[item.address.toLowerCase()] || 0) < 1" #attachment>
                      <HalloweenGenericButton variant="embedded" icon-only @click="onCraftButtonClick">
                        <template #icon>
                          <Hammer :size="20" />
                        </template>
                      </HalloweenGenericButton>
                    </template>
                  </UIItemDisplay>
                </div>
              </HalloweenItemFrame>
            </div>
          </UIHalloweenAccordion>
        </div>

        <div class="contents">
          <HalloweenItemFrame variant="translucent">
            <template #title>{{ $t('halloweenModalExpeditionLocationSelectRewardsTitle') }}</template>

            <UIItemDisplay
              v-for="item in locationItemsList"
              :key="item.id"
              :size="60"
              :item="item"
              direction="horizontal"
              variant="default"
              tooltip
            />
          </HalloweenItemFrame>
        </div>
      </div>
    </div>

    <template v-if="!simplified" #footer>
      <div class="modal-footer">
        <HalloweenGenericButton
          :disabled="halloween.$state.statefulUserExpeditions.length > 0"
          icon-only
          @click="onLocationChange"
        >
          <template #icon>
            <IconsChevronRight style="width: 20px; height: 20px; transform: rotate(180deg)" />
          </template>
        </HalloweenGenericButton>

        <HalloweenGenericButton @click="onExpeditionStart">
          {{ $t('halloweenModalExpeditionLocationSelectButton') }}
        </HalloweenGenericButton>

        <HalloweenGenericButton
          :disabled="halloween.$state.statefulUserExpeditions.length > 0"
          icon-only
          @click="onLocationChange"
        >
          <template #icon>
            <IconsChevronRight style="width: 20px; height: 20px" />
          </template>
        </HalloweenGenericButton>
      </div>
    </template>
  </UIModal>
</template>
<script setup lang="ts">
import { BigNumber } from 'bignumber.js';
import UIHalloweenAccordion from '~/components/Halloween/ui/UIHalloweenAccordion.vue';
import UIItemDisplay from '~/components/Halloween/ui/UIItemDisplay.vue';
import Hammer from '~/components/icons/Hammer.vue';
import { HalloweenItem, type ItemDescription } from '~/types/halloween/items';
import type { HalloweenLocation } from '~/types/halloween/location';
import halloweenItemRegistry from '~/utils/halloween/halloween-item-registry';
import { Buildings } from '~/utils/constants';

const halloween = useHalloweenStore();
const mainStore = useMainStore();

const locationTokenBalances = computed(() => {
  const balances = locationConfig.value?.config.map((item) => {
    return [
      item.address.toLowerCase(),
      new BigNumber(
        halloween.$state.tokenBalances.find((t) => t.tokenAddress.toLowerCase() === item.address.toLowerCase())
          ?.formattedBalance || '0'
      )
        .dp(2)
        .toNumber()
    ];
  });

  return Object.fromEntries(balances || []);
});

const isOpen = defineModel<boolean>({ required: true, default: false });

const switchTimeout = ref<any>(null);
const isSwitching = ref(false);

const emit = defineEmits<{
  (e: 'craftButtonClick', item: ItemDescription): void;
  (e: 'expeditionStart', location: HalloweenLocation): void;
  (e: 'closed'): void;
}>();

const props = defineProps<{
  simplified?: boolean;
}>();

const location = computed(() => halloween.$state.runtimeSelectedLocation);

const locationRequiredItem = computed(() => {
  return location.value === 'forest'
    ? halloweenItemRegistry.get(HalloweenItem.Axe)
    : halloweenItemRegistry.get(HalloweenItem.Torch);
});

const locationConfig = computed(() => halloween.$state.locationConfigurations[location.value]);

const locationItemsList = computed(() => {
  const list = halloween.$state.locationExpectedRewardTokens[location.value] || [];
  return list.map((item) => halloweenItemRegistry.get(item.token)) as ItemDescription[];
});

const { t } = useI18n();
const locationDescriptions: ComputedRef<Record<string, string[]>> = computed(() => ({
  cave: [
    t('halloweenModalExpeditionLocationSelectCaveDescription'),
    t('halloweenModalExpeditionLocationSelectRequirementsTextTitle')
  ],
  forest: [
    t('halloweenModalExpeditionLocationSelectForestDescription'),
    t('halloweenModalExpeditionLocationSelectRequirementsTextTitle')
  ]
}));

const locationDescription = computed(() => locationDescriptions.value[location.value]);

const onExpeditionStart = () => {
  isOpen.value = false;
  emit('expeditionStart', location.value as any);
};

const onCraftButtonClick = () => {
  isOpen.value = false;
  emit('craftButtonClick', locationRequiredItem.value);
};

const onLocationChange = () => {
  if (isSwitching.value) return;
  const nextLocation = location.value === 'forest' ? 'cave' : 'forest';

  isSwitching.value = true;
  if (switchTimeout.value) clearTimeout(switchTimeout.value);
  switchTimeout.value = setTimeout(() => {
    halloween.$state.runtimeSelectedLocation = nextLocation;
    isSwitching.value = false;
  }, 300);
};

watch(
  [isOpen, location, () => props.simplified],
  ([modalOpen, currentLocation, isSimplified]: [boolean, HalloweenLocation, boolean | undefined]) => {
    if (modalOpen && currentLocation && !isSimplified) {
      const buildingKey = currentLocation === 'forest' ? Buildings.Forest : Buildings.Cave;
      mainStore.openedBuilding = buildingKey;
    } else if (!modalOpen && !isSimplified) {
      mainStore.openedBuilding = null;
    }
  },
  { immediate: true }
);
</script>
<style scoped>
.modal-body {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;

  width: 910px;
  height: 430px;

  max-width: 100%;
  overflow: hidden;
  border-radius: 0 0 12px 12px;

  .modal-body-wrapper {
    width: 100%;
    height: 100%;
    transition: all 0.3s ease;
    padding: 18px;
  }

  &.switching {
    .modal-body-wrapper {
      transform: translateY(-20px) scale(0.95);
      opacity: 0;
    }

    .modal-body-background {
      opacity: 0;
    }
  }

  &.simplified {
    height: min(100vh, 510px);
  }

  @media screen and (max-width: 640px) {
    height: 100% !important;
  }
}

.modal-body-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  pointer-events: none;
  transition: all 0.3s ease;
}

.contents {
  position: absolute;
  display: grid;
  bottom: 14px;
  left: 0;
  width: 100%;
  place-items: center;
}

.accordion {
  width: 100%;
  height: calc(100% - 120px);
  font-family:
    PT Serif,
    serif;

  .accordion-content {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: fit-content;
    gap: 12px;
    text-align: center;
    justify-content: center;
    align-items: center;
  }

  .accordion-content-craft-frame {
    position: relative;
    display: flex;
    flex-direction: row;
    gap: 16px;
    width: fit-content;
    height: fit-content;
  }
}

.modal-footer {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  gap: 20px;
}

.modal-header {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 11px;
  justify-content: center;
  align-items: center;

  .modal-header-primary {
    font-family:
      PT Serif,
      serif;
    font-weight: 700;
    font-size: 32px;
    line-height: 30px;
    text-align: center;
    vertical-align: middle;

    color: #ffffff;
  }

  .modal-header-secondary {
    font-family:
      PT Serif,
      serif;
    font-weight: 400;
    font-size: 20px;
    line-height: 110.00000000000001%;
    text-align: center;
    vertical-align: bottom;
    color: #90989d;
  }
}
</style>
